import { GoBackHeaderTitle } from "components";
import { GET_LIST_REQUEST_DEFAULT_LIMIT } from "constants/common";
import { t } from "i18next";
import moment from "moment";
import { ORDER_STATUSES_LIST } from "modules/orders";
import { useState } from "react";
import Skeleton from "react-loading-skeleton";
import { Pagination, PersonInfo, Status } from "ui";

import { useGetResellerReferralOrders } from "../../../apis";
import { ProfileSummaryCards } from "../../../components";
import { formatAmountInFcfa } from "../../../helpers";
import { ResellerReferral } from "../../../resellers.types";
import ReferralOrderRow from "./ReferralOrderRow";

import styles from "./ReferralOrders.module.scss";

interface ReferralOrdersProps {
  resellerId: string;
  referralId: string;
  referral?: ResellerReferral;
  onGoBack: () => void;
  onOpenOrder: (orderId: string) => void;
}

const ReferralOrders = ({
  resellerId,
  referralId,
  referral,
  onGoBack,
  onOpenOrder
}: ReferralOrdersProps) => {
  const [page, setPage] = useState(1);

  const {
    orders,
    meta,
    totalSpent,
    commissionGenerated,
    lastOrderAt,
    isLoading
  } = useGetResellerReferralOrders(resellerId, referralId, {
    page,
    take: GET_LIST_REQUEST_DEFAULT_LIMIT
  });

  const orderStatusesList: Status[] = ORDER_STATUSES_LIST.map((status) => ({
    ...status,
    onClick: () => {}
  }));

  const referralName = referral
    ? `${referral.firstname} ${referral.lastname}`
    : t("Referee");

  return (
    <div className={styles.referralOrders}>
      <div className={styles.referralOrders_header}>
        <GoBackHeaderTitle title={t("All referees")} onGoBack={onGoBack} />

        <div className={styles.referralOrders_person}>
          <PersonInfo
            image={referral?.imageUrl ? { url: referral.imageUrl } : null}
            dimensions={{ width: 48, height: 48, borderRadius: "100%" }}
            personName={referralName}
            nameClassName={styles.referralOrders_personName}
            bottomText={referral?.phoneNumber}
            bottomTextClassName={styles.referralOrders_personPhone}
          />
        </div>
      </div>

      <ProfileSummaryCards
        isLoading={isLoading && !orders}
        cards={[
          {
            label: "Orders",
            value: meta?.itemCount ?? 0,
            hint: referral?.codeUsed
              ? `${t("Signed up with")} ${referral.codeUsed}`
              : undefined
          },
          {
            label: "Total spent",
            value: formatAmountInFcfa(totalSpent),
            hint: t("Cancelled and failed orders excluded")
          },
          {
            label: "Commission generated",
            value: formatAmountInFcfa(commissionGenerated),
            accent: "primary"
          },
          {
            label: "Last order",
            value: lastOrderAt
              ? moment(lastOrderAt).format("DD/MM/YYYY")
              : "—"
          }
        ]}
      />

      {isLoading && !orders ? (
        <Skeleton
          className={styles.referralOrders_skeleton}
          count={4}
        />
      ) : orders?.length ? (
        <div className={styles.referralOrders_list}>
          {orders.map((order) => (
            <ReferralOrderRow
              key={order.id}
              order={order}
              orderStatusesList={orderStatusesList}
              onOpenOrder={onOpenOrder}
            />
          ))}
        </div>
      ) : (
        <div className={styles.referralOrders_empty}>
          {t("This referee has not placed an order yet")}
        </div>
      )}

      <Pagination
        currentPage={page}
        totalItems={meta?.itemCount ?? 0}
        onChange={setPage}
      />
    </div>
  );
};

export default ReferralOrders;
